import React, { useEffect, useRef, useState } from "react";
import { Delete, Plus, Percent, Euro, X as XIcon } from "lucide-react";
import ScontrinoGestureLabel from "./ScontrinoGestureLabel";

// Pulsante CL con conferma a 2 tocchi (timeout 3s)
export function ClearAllButton({ onConfirm, testid = "ckp-cl" }) {
  const [arm, setArm] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const tap = () => {
    if (!arm) {
      setArm(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setArm(false), 3000);
      return;
    }
    clearTimeout(timer.current);
    setArm(false);
    onConfirm?.();
  };
  return (
    <div>
      <button
        data-testid={testid}
        onClick={tap}
        className={`w-full h-16 rounded-md border-[3px] font-mono font-bold btn-tactile transition-colors ${
          arm
            ? "border-red-600 bg-red-600 text-white text-xl tracking-widest animate-pulse"
            : "border-red-500 bg-white text-red-600 text-2xl tracking-[0.3em] hover:bg-red-50"
        }`}
      >
        {arm ? "Confermi?" : "CL"}
      </button>
      <div className="text-[9px] font-mono text-center text-velta-mute mt-1 uppercase tracking-widest">
        {arm ? "Tocca di nuovo entro 3s per azzerare" : "Azzera tutto il conto"}
      </div>
    </div>
  );
}

/**
 * Tastierino cassa avanzato:
 * - importo digitato + tocco articolo = prezzo manuale
 * - importo digitato + tocco categoria = vendita rapida
 * - quantità + [×] + articolo = moltiplicatore
 * - [+] prezzo libero · [%]/[€] sconto (conto intero o riga selezionata)
 * - banconota digitata + CONTANTI = calcolo resto
 */
export default function CheckoutKeypad({
  value,
  onValueChange,
  multiplier,
  onMultiplierChange,
  onFreePrice,
  onDiscountPct,
  onDiscountEur,
  selectedLineName = null,
}) {
  const numeric = parseFloat(value || 0) || 0;

  const push = (c) => {
    const v = String(value || "");
    if (c === ".") {
      if (v.includes(".")) return;
      onValueChange(v === "" ? "0." : v + ".");
      return;
    }
    if (v.includes(".")) {
      const dec = v.split(".")[1] || "";
      if (dec.length >= 2) return;
    }
    onValueChange((v + c).replace(/^0(?=\d)/, ""));
  };
  const back = () => onValueChange(String(value || "").slice(0, -1));

  const doMult = () => {
    if (numeric >= 1) {
      onMultiplierChange(Math.min(999, Math.round(numeric)));
      onValueChange("");
    } else if (multiplier) {
      onMultiplierChange(null);
    }
  };
  const doFree = () => {
    if (numeric <= 0) return;
    onFreePrice?.(numeric);
    onValueChange("");
  };
  const doPct = () => {
    if (numeric <= 0 || numeric > 100) return;
    onDiscountPct?.(numeric);
    onValueChange("");
  };
  const doEur = () => {
    if (numeric <= 0) return;
    onDiscountEur?.(numeric);
    onValueChange("");
  };

  const displayValue = value ? `€ ${numeric.toFixed(2).replace(".", ",")}` : null;

  let indicator = null;
  if (selectedLineName) {
    indicator = {
      cls: "bg-violet-50 border-violet-200 text-violet-700",
      text: numeric > 0 ? `Sconto su "${selectedLineName}": % o €` : `Riga: ${selectedLineName} — digita sconto poi % o €`,
    };
  } else if (multiplier && numeric > 0) {
    indicator = { cls: "bg-blue-50 border-blue-200 text-blue-700", text: `${multiplier} × prossimo articolo a € ${numeric.toFixed(2).replace(".", ",")}` };
  } else if (multiplier) {
    indicator = { cls: "bg-blue-50 border-blue-200 text-blue-700", text: `Moltiplicatore: ${multiplier} × prossimo articolo` };
  } else if (numeric > 0) {
    indicator = { cls: "bg-amber-50 border-amber-200 text-amber-700", text: "articolo: prezzo · categoria: rapida · contanti: resto" };
  }

  const numKey = (k, label) => (
    <button
      key={k}
      data-testid={`ckp-${k === "." ? "comma" : k}`}
      onClick={() => push(k)}
      className="h-14 rounded bg-white border border-velta-border hover:border-velta-ink font-mono font-bold text-xl btn-tactile active:scale-95"
    >
      {label || k}
    </button>
  );

  return (
    <div className="bg-white border border-velta-border rounded-md p-2.5 flex flex-col gap-2" data-testid="checkout-keypad">
      <ScontrinoGestureLabel className="px-1" />
      <div className="h-14 bg-velta-bg border border-velta-border rounded flex items-center justify-between px-3">
        <span className="text-[9px] font-mono uppercase tracking-widest text-velta-mute">Digita</span>
        <span data-testid="ckp-display" className="font-mono font-bold text-2xl tabular-nums text-velta-ink">
          {multiplier && !value ? (
            `${multiplier} ×`
          ) : displayValue ? (
            multiplier ? `${multiplier} × ${displayValue}` : displayValue
          ) : (
            <span className="text-velta-mute/40 text-sm tracking-widest">0,00</span>
          )}
        </span>
      </div>
      <div
        data-testid="ckp-mode-indicator"
        className={`h-6 flex items-center justify-center rounded border text-[10px] font-mono uppercase tracking-wider px-1 truncate ${
          indicator ? indicator.cls : "border-transparent text-transparent"
        }`}
      >
        {indicator ? indicator.text : "·"}
      </div>
      <div className="grid grid-cols-4 gap-1.5">
        {numKey("7")}{numKey("8")}{numKey("9")}
        <button data-testid="ckp-back" onClick={back} className="h-14 rounded bg-velta-bg border border-velta-border hover:border-velta-ink flex items-center justify-center btn-tactile active:scale-95">
          <Delete className="w-5 h-5" />
        </button>
        {numKey("4")}{numKey("5")}{numKey("6")}
        <button
          data-testid="ckp-mult"
          onClick={doMult}
          disabled={numeric < 1 && !multiplier}
          className={`h-14 rounded font-mono font-bold text-xl flex items-center justify-center btn-tactile active:scale-95 disabled:opacity-30 ${
            multiplier ? "bg-blue-600 text-white" : "bg-[#1E2D5B] text-white hover:bg-[#162244]"
          }`}
          title="Moltiplicatore quantità: digita quantità, tocca ×, poi l'articolo"
        >
          {multiplier ? <XIcon className="w-5 h-5" /> : "×"}
        </button>
        {numKey("1")}{numKey("2")}{numKey("3")}
        <button
          data-testid="ckp-pct"
          onClick={doPct}
          disabled={numeric <= 0 || numeric > 100}
          className="h-14 rounded bg-[#2F9E77] hover:bg-[#27855F] text-white flex items-center justify-center btn-tactile disabled:opacity-30 active:scale-95"
          title="Sconto % (sul conto, o sulla riga selezionata)"
        >
          <Percent className="w-5 h-5" />
        </button>
        {numKey(".", ",")}{numKey("0")}{numKey("00")}
        <button
          data-testid="ckp-eur"
          onClick={doEur}
          disabled={numeric <= 0}
          className="h-14 rounded bg-[#0F766E] hover:bg-[#0d5f58] text-white flex items-center justify-center btn-tactile disabled:opacity-30 active:scale-95"
          title="Sconto € (sul conto, o sulla riga selezionata)"
        >
          <Euro className="w-5 h-5" />
        </button>
        <button
          data-testid="ckp-free"
          onClick={doFree}
          disabled={numeric <= 0}
          className="col-span-4 h-10 rounded bg-velta-bg border border-velta-border hover:border-velta-ink text-velta-ink text-xs font-medium flex items-center justify-center gap-1.5 btn-tactile disabled:opacity-30 active:scale-95"
          title="Aggiungi come Prezzo Libero"
        >
          <Plus className="w-4 h-4" /> Prezzo libero
        </button>
      </div>
      <div className="text-[9px] font-mono text-velta-mute px-1 leading-relaxed">
        [×] quantità · [%]/[€] sconto (conto o riga selezionata) · banconota + CONTANTI = resto
      </div>
    </div>
  );
}
